import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

/**
 * Super-Owner only. Records a HISTORICAL sale that was wiped from
 * movement_logs but whose stock decrement already happened. Inserts a sale
 * movement_log + paid receivable WITHOUT touching product current_stock.
 *
 * Props:
 *  - onClose:    () => void — close the dialog (clicking backdrop or Cancel)
 *  - onApplied:  () => void — fired after a successful POST so the parent
 *                  can re-fetch overview + bump RevenueWidget
 */
export default function BackfillSaleDialog({ onClose, onApplied }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    product_sku: "TSM10",
    quantity: 2,
    sale_price: 100,
    sale_date: "2026-06-16",
    seller_type: "owner",
  });
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const total = (Number(form.quantity) || 0) * (Number(form.sale_price) || 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backfill-sale", {
        product_sku: form.product_sku.trim(),
        quantity: Number(form.quantity),
        sale_price: Number(form.sale_price),
        sale_date: form.sale_date,
        seller_type: form.seller_type,
      });
      if (data.created) {
        toast.success(
          t("backfill.toast_created", {
            qty: data.quantity,
            name: data.product_name,
            price: Number(data.sale_price).toFixed(2),
            total: Number(data.total_amount).toFixed(2),
          })
        );
      } else {
        toast.info(t("backfill.toast_noop"));
      }
      onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="backfill-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">
          {t("backfill.title")}
        </h2>
        <p className="text-sm text-zinc-600 mt-1 mb-4">{t("backfill.subtitle")}</p>

        {confirming ? (
          <div className="space-y-4">
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900"
              data-testid="backfill-confirm-text"
            >
              {t("backfill.confirm_body", {
                qty: form.quantity,
                sku: form.product_sku,
                price: Number(form.sale_price).toFixed(2),
                date: form.sale_date,
                total: total.toFixed(2),
              })}
            </div>
            <div className="flex gap-3">
              <BrutalButton
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setConfirming(false)}
                disabled={busy}
                data-testid="backfill-cancel-confirm"
              >
                {t("common.cancel")}
              </BrutalButton>
              <BrutalButton
                type="button"
                className="flex-1"
                onClick={submit}
                disabled={busy}
                data-testid="backfill-confirm-submit"
              >
                {busy ? t("backfill.running") : t("backfill.confirm_run")}
              </BrutalButton>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.product_sku.trim()) {
                toast.error(t("backfill.sku_required"));
                return;
              }
              if (!Number(form.quantity) || Number(form.quantity) < 1) {
                toast.error(t("backfill.qty_required"));
                return;
              }
              setConfirming(true);
            }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-semibold mb-1">
                {t("backfill.field_sku")}
              </label>
              <BrutalInput
                type="text"
                value={form.product_sku}
                onChange={(e) => setForm({ ...form, product_sku: e.target.value })}
                placeholder="TSM10"
                maxLength={120}
                data-testid="backfill-sku-input"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">
                  {t("backfill.field_qty")}
                </label>
                <BrutalInput
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={(e) =>
                    setForm({ ...form, quantity: e.target.value })
                  }
                  data-testid="backfill-qty-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">
                  {t("backfill.field_price")}
                </label>
                <BrutalInput
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.sale_price}
                  onChange={(e) =>
                    setForm({ ...form, sale_price: e.target.value })
                  }
                  data-testid="backfill-price-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                {t("backfill.field_date")}
              </label>
              <BrutalInput
                type="date"
                value={form.sale_date}
                onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                data-testid="backfill-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">
                {t("backfill.field_seller")}
              </label>
              <select
                className="input-brutal w-full"
                value={form.seller_type}
                onChange={(e) =>
                  setForm({ ...form, seller_type: e.target.value })
                }
                data-testid="backfill-seller-select"
              >
                <option value="owner">{t("backfill.seller_owner")}</option>
                <option value="distributor">
                  {t("backfill.seller_distributor")}
                </option>
              </select>
            </div>
            <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md p-2.5">
              {t("backfill.implied_total")}:{" "}
              <span
                className="font-semibold text-zinc-900 tabular-nums"
                data-testid="backfill-implied-total"
              >
                ${total.toFixed(2)}
              </span>
            </div>
            <div className="flex gap-3 pt-2">
              <BrutalButton
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={busy}
              >
                {t("common.cancel")}
              </BrutalButton>
              <BrutalButton
                type="submit"
                className="flex-1"
                disabled={busy}
                data-testid="backfill-review-btn"
              >
                {t("backfill.review_btn")}
              </BrutalButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
